import { useMemo, useState } from "react";
import { buildTaskCompleteOperation } from "../operations/workerOperations";
import { getOrCreateDeviceId } from "../sync/deviceIdentity";
import { persistAndSyncOperation } from "../sync/operationRuntime";
import type { OutboxSummary } from "../sync/types";
import { supabase } from "../auth/supabaseClient";
import { ConfirmationSheet } from "./ConfirmationSheet";
import type { WorkItem } from "./model";
import { publicTaskTitle } from "./taskDisplay";

interface TodayTaskCompletionPanelProps {
  farmId: string;
  userId: string;
  items: readonly WorkItem[];
  preferredTaskIds?: readonly string[];
  onSyncSummary?: (summary: OutboxSummary) => void;
  onTaskStatus?: (taskId: string, status: "done" | "conflict", summary: OutboxSummary) => void;
}

interface PendingTask {
  item: WorkItem;
  notes: string;
}

function syncMessage(status: string): string {
  if (status === "synced") return "کار انجام‌شده ثبت و همگام شد.";
  if (status === "pending") return "انجام کار آفلاین ثبت شد و در صف ارسال قرار گرفت.";
  if (status === "retryable_error") return "ثبت محلی حفظ شد؛ ارسال دوباره انجام می‌شود.";
  if (status === "terminal_conflict") return "این کار تغییر کرده و نیاز به بررسی تعارض دارد.";
  return "وضعیت کار ثبت شد.";
}

export function TodayTaskCompletionPanel({
  farmId,
  userId,
  items,
  preferredTaskIds,
  onSyncSummary,
  onTaskStatus,
}: TodayTaskCompletionPanelProps) {
  const tasks = useMemo(
    () =>
      items.filter(
        (item) =>
          item.kind === "task" &&
          item.status !== "done" &&
          item.canComplete === true &&
          Number.isInteger(item.domainVersion) &&
          (item.domainVersion ?? 0) > 0 &&
          (!preferredTaskIds?.length || preferredTaskIds.includes(item.id)),
      ),
    [items, preferredTaskIds],
  );
  const [notesByTask, setNotesByTask] = useState<Record<string, string>>({});
  const [pending, setPending] = useState<PendingTask>();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();

  async function confirmTask() {
    if (!pending?.item.domainVersion) return;

    try {
      setBusy(true);
      setError(undefined);
      const operation = buildTaskCompleteOperation({
        actorId: userId,
        farmId,
        deviceId: getOrCreateDeviceId(),
        taskId: pending.item.id,
        expectedVersion: pending.item.domainVersion,
        performedAt: new Date().toISOString(),
        ...(pending.notes.trim() ? { completionNotes: pending.notes.trim() } : {}),
      });

      const result = await persistAndSyncOperation(
        supabase,
        operation,
        typeof navigator === "undefined" ? true : navigator.onLine !== false,
      );

      onSyncSummary?.(result.summary);
      const finalStatus = result.operation.status === "terminal_conflict" ? "conflict" : "done";
      onTaskStatus?.(pending.item.id, finalStatus, result.summary);
      setMessage(syncMessage(result.operation.status));
      setNotesByTask((current) => ({ ...current, [pending.item.id]: "" }));
      setPending(undefined);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "ثبت انجام کار ناموفق بود.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="section-card" aria-labelledby="task-complete-title">
      <div className="section-title-row">
        <div>
          <p className="section-kicker">کارهای دستی</p>
          <h2 id="task-complete-title">ثبت انجام کار</h2>
        </div>
        <span className="count-pill">{tasks.length}</span>
      </div>

      {tasks.length === 0 ? (
        <p className="empty-state">کار دستی قابل تکمیل برای این حساب وجود ندارد.</p>
      ) : (
        <div className="work-list">
          {tasks.map((item) => (
            <div className={`work-item ${item.status}`} key={item.id}>
              <span className="work-main">
                <strong>{publicTaskTitle(item.title)}</strong>
                <span>{item.dueLabel ? `موعد ${item.dueLabel}` : "بدون زمان مشخص"}</span>
                <input
                  aria-label={`یادداشت تکمیل ${publicTaskTitle(item.title)}`}
                  placeholder="یادداشت اختیاری"
                  value={notesByTask[item.id] ?? ""}
                  onChange={(event) =>
                    setNotesByTask((current) => ({
                      ...current,
                      [item.id]: event.target.value,
                    }))
                  }
                />
              </span>
              <button
                type="button"
                className="secondary-button"
                disabled={busy}
                onClick={() => {
                  setMessage(undefined);
                  setError(undefined);
                  setPending({
                    item,
                    notes: notesByTask[item.id] ?? "",
                  });
                }}
              >
                انجام شد
              </button>
            </div>
          ))}
        </div>
      )}

      {message ? <p className="operation-success">{message}</p> : null}
      {error ? <p className="settings-warning">{error}</p> : null}

      {pending ? (
        <ConfirmationSheet
          title="تأیید انجام کار"
          parts={[
            publicTaskTitle(pending.item.title),
            pending.item.dueLabel ? `موعد ${pending.item.dueLabel}` : "بدون زمان مشخص",
            ...(pending.notes.trim() ? [`یادداشت: ${pending.notes.trim()}`] : []),
          ]}
          speechEnabled
          fastConfirmEnabled
          onCancel={() => setPending(undefined)}
          onConfirm={() => void confirmTask()}
        />
      ) : null}
    </section>
  );
}
